import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { isLegacyManualTarget, LegacyManualSizeImport } from "@/components/orders/LegacyManualSizeImport";
import { UnnotifiedLeadBadge } from "@/components/UnnotifiedLeadBadge";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ShoppingCart,
  Search,
  Filter,
  MoreVertical,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Truck,
  DollarSign,
  User,
  Phone,
  Mail,
  MapPin,
  ArrowLeft,
  Package,
  Calendar,
  ExternalLink,
  Loader2,
  Camera,
  FileDown,
  Smartphone,
  Factory,
  Send
} from "lucide-react";

const ORDER_STATUSES = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-700", icon: Package },
  shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-700", icon: Truck },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: XCircle }
};

const ORDER_TYPES = {
  cabinet_order: { label: "Cabinet Order", color: "bg-emerald-100 text-emerald-700" },
  consultation: { label: "Consultation", color: "bg-sky-100 text-sky-700" },
  refacing_quote: { label: "Refacing Quote", color: "bg-amber-100 text-amber-700" },
  other: { label: "Other", color: "bg-slate-100 text-slate-700" }
};

export default function OrdersPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  
  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFullOrderView, setShowFullOrderView] = useState(false);
  const [showOrderActionModal, setShowOrderActionModal] = useState(false);
  const [orderZoom, setOrderZoom] = useState(80);
  const [showSendProductionModal, setShowSendProductionModal] = useState(false);
  const [productionTab, setProductionTab] = useState("aline");
  const [productionSuppliers, setProductionSuppliers] = useState([]);
  const [selectedProductionSuppliers, setSelectedProductionSuppliers] = useState([]);
  const [sendingProduction, setSendingProduction] = useState(false);
  const [routingToProductionSheet, setRoutingToProductionSheet] = useState(false);

  useEffect(() => {
    fetchOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data || []);
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success("Order status updated");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const fetchProductionSuppliers = async (category) => {
    if (category === "aline") return;
    try {
      const res = await axios.get(`${API}/suppliers?category=${category}`);
      setProductionSuppliers(res.data || []);
    } catch (err) {
      setProductionSuppliers([]);
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const isProductionDispatched = Boolean(
    selectedOrder?.production_sheet_id
    || selectedOrder?.production_sheet?.status === "dispatched"
  );

  const sendOrderToProductionSheet = async () => {
    if (!selectedOrder || isProductionDispatched) return;
    setRoutingToProductionSheet(true);
    try {
      const response = await axios.post(`${API}/orders/${selectedOrder.order_id}/production-sheet`);
      const transfer = response.data;
      const productionFields = {
        project_id: transfer.project.project_id,
        production_sheet_id: transfer.production_sheet.sheet_id,
        production_sheet: transfer.production_sheet,
      };
      setSelectedOrder(current => ({ ...current, ...productionFields }));
      setOrders(current => current.map(order => (
        order.order_id === selectedOrder.order_id ? { ...order, ...productionFields } : order
      )));
      if (transfer.idempotent) {
        toast.info(`Already linked to ${transfer.production_sheet.sheet_number}`);
      } else {
        toast.success(
          `${transfer.production_sheet.sheet_number} created · ${transfer.derived.hinge_baseplate_packages || 0} hinge packages · ${transfer.derived.salice_slide_pairs || 0} Salice slide pairs`
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send this order to the production sheet");
    } finally {
      setRoutingToProductionSheet(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone?.includes(searchTerm) ||
      order.order_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const matchesType = typeFilter === "all" || order.order_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Calculate stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    totalValue: orders.reduce((sum, o) => sum + (o.order_value || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-indigo-600" />
            Orders
          </h1>
          <p className="text-slate-600 mt-1">
            {stats.total} orders • {stats.pending} pending • {formatCurrency(stats.totalValue)} total value
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="order-search"
                />
              </div>
            </div>
            <div className="w-40">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="orders-status-filter-trigger">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent data-testid="orders-status-filter-menu">
                  <SelectItem value="all" data-testid="orders-status-filter-option-all">All Statuses</SelectItem>
                  {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} data-testid={`orders-status-filter-option-${key}`}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="orders-type-filter-trigger">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent data-testid="orders-type-filter-menu">
                  <SelectItem value="all" data-testid="orders-type-filter-option-all">All Types</SelectItem>
                  {Object.entries(ORDER_TYPES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} data-testid={`orders-type-filter-option-${key}`}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {filteredOrders.length > 0 ? (
            <>
            <div className="hidden overflow-x-auto md:block">
            <Table className="w-full table-fixed md:min-w-[800px] md:table-auto">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="w-[110px]">Type</TableHead>
                  <TableHead className="w-[90px]">Value</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[150px]">Date</TableHead>
                  <TableHead className="w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const statusInfo = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
                  const typeInfo = ORDER_TYPES[order.order_type] || ORDER_TYPES.other;
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <TableRow key={order.order_id} data-testid={`order-row-${order.order_id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => viewOrderDetails(order)}>
                      <TableCell>
                        <div className="font-medium text-slate-900">
                          #{order.order_id?.slice(-8).toUpperCase()}
                        </div>
                        {order.external_order_id && (
                          <div className="text-xs text-slate-500 truncate max-w-[100px]" title={order.external_order_id}>
                            Ext: {order.external_order_id.length > 12 ? order.external_order_id.slice(0, 12) + "..." : order.external_order_id}
                          </div>
                        )}
                        <div className="mt-1">
                          <UnnotifiedLeadBadge status={order.admin_notification_status} recordId={order.order_id} context="order-table" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <div className="font-medium">{order.customer_name}</div>
                            <div className="text-xs text-slate-500">{order.customer_email || order.customer_phone}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color} data-testid={`order-type-${order.order_id}`}>
                          {typeInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-green-600" data-testid={`order-value-${order.order_id}`}>
                          {formatCurrency(order.order_value)}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={order.status}
                          onValueChange={(value) => updateOrderStatus(order.order_id, value)}
                        >
                          <SelectTrigger className={`w-32 h-8 text-xs ${statusInfo.color}`} data-testid={`order-status-trigger-${order.order_id}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent data-testid={`order-status-menu-${order.order_id}`}>
                            {Object.entries(ORDER_STATUSES).map(([key, { label }]) => (
                              <SelectItem key={key} value={key} data-testid={`order-status-option-${order.order_id}-${key}`}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-600">
                          {formatDate(order.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewOrderDetails(order)}
                          data-testid={`view-order-desktop-button-${order.order_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <div className="divide-y divide-slate-200 md:hidden" data-testid="orders-mobile-list">
              {filteredOrders.map(order => {
                const statusInfo = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
                const typeInfo = ORDER_TYPES[order.order_type] || ORDER_TYPES.other;
                const StatusIcon = statusInfo.icon;
                return (
                  <div key={`mobile-${order.order_id}`} className="space-y-3 p-4" data-testid={`order-mobile-card-${order.order_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-xs font-semibold text-slate-500">#{order.order_id?.slice(-8).toUpperCase()}</p>
                        <p className="truncate font-semibold text-slate-950">{order.customer_name}</p>
                        <p className="truncate text-xs text-slate-500">{order.customer_email || order.customer_phone}</p>
                        <div className="mt-1">
                          <UnnotifiedLeadBadge status={order.admin_notification_status} recordId={order.order_id} context="order-mobile" />
                        </div>
                      </div>
                      <p className="shrink-0 font-mono font-bold text-emerald-700" data-testid={`order-mobile-value-${order.order_id}`}>{formatCurrency(order.order_value)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={typeInfo.color} data-testid={`order-mobile-type-${order.order_id}`}>{typeInfo.label}</Badge>
                      <Badge className={statusInfo.color} data-testid={`order-mobile-status-${order.order_id}`}><StatusIcon className="mr-1 h-3 w-3" />{statusInfo.label}</Badge>
                      <span className="text-xs text-slate-500">{formatDate(order.created_at)}</span>
                    </div>
                    <Button variant="outline" size="sm" className="w-full" onClick={() => viewOrderDetails(order)} data-testid={`view-order-mobile-button-${order.order_id}`}>
                      <Eye className="mr-2 h-4 w-4" />View order specification
                    </Button>
                  </div>
                );
              })}
            </div>
            </>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">No orders found</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== "all" || typeFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "Orders from your website will appear here"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden p-0 flex flex-col">
          <div className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              Order Details
            </DialogTitle>
            <DialogDescription className="sr-only">
              Review the selected order, its customer information, items, and production actions.
            </DialogDescription>
          </div>
          
          {selectedOrder && (
            <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* Order Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    #{selectedOrder.order_id?.slice(-8).toUpperCase()}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {formatDate(selectedOrder.created_at)}
                  </p>
                  <div className="mt-2">
                    <UnnotifiedLeadBadge status={selectedOrder.admin_notification_status} recordId={selectedOrder.order_id} context="order-detail" />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(selectedOrder.order_value)}
                  </div>
                  <Badge className={ORDER_TYPES[selectedOrder.order_type]?.color || "bg-slate-100"}>
                    {ORDER_TYPES[selectedOrder.order_type]?.label || selectedOrder.order_type}
                  </Badge>
                </div>
              </div>

              {/* Quote Details */}
              {(selectedOrder.quote_id || selectedOrder.door_style || selectedOrder.finish || selectedOrder.cabinet_count) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Quote Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      {selectedOrder.quote_id && (
                        <div><span className="text-slate-500">Quote ID:</span> <span className="font-medium">{selectedOrder.quote_id}</span></div>
                      )}
                      {selectedOrder.door_style && (
                        <div><span className="text-slate-500">Door Style:</span> <span className="font-medium">{selectedOrder.door_style}</span></div>
                      )}
                      {selectedOrder.finish && (
                        <div><span className="text-slate-500">Finish:</span> <span className="font-medium">{selectedOrder.finish}</span></div>
                      )}
                      {selectedOrder.cabinet_count && (
                        <div><span className="text-slate-500">Cabinets:</span> <span className="font-medium">{selectedOrder.cabinet_count}</span></div>
                      )}
                      {selectedOrder.delivery_option && (
                        <div><span className="text-slate-500">Delivery:</span> <span className="font-medium capitalize">{selectedOrder.delivery_option?.replace(/_/g, " ")}</span></div>
                      )}
                      {selectedOrder.payment_option && (
                        <div><span className="text-slate-500">Payment:</span> <span className="font-medium capitalize">{selectedOrder.payment_option?.replace(/_/g, " ")}</span></div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Customer Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{selectedOrder.customer_name}</span>
                  </div>
                  {selectedOrder.customer_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${selectedOrder.customer_email}`} className="text-blue-600 hover:underline" data-testid="order-customer-email-link">
                        {selectedOrder.customer_email}
                      </a>
                    </div>
                  )}
                  {selectedOrder.customer_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${selectedOrder.customer_phone}`} className="text-blue-600 hover:underline" data-testid="order-customer-phone-link">
                        {selectedOrder.customer_phone}
                      </a>
                    </div>
                  )}
                  {selectedOrder.customer_address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{selectedOrder.customer_address}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Order Items */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Order Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedOrder.items.map((item, index) => (
                        <div key={`item-${item.name}-${index}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {item.details?.code && (
                                <Badge variant="outline" className="text-xs font-mono">{item.details.code}</Badge>
                              )}
                              <span className="font-medium">{item.name}</span>
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                              <span>Qty: {item.quantity}</span>
                              {item.details?.width && item.details?.height && (
                                <span>| {item.details.width}"W x {item.details.height}"H{item.details.depth ? ` x ${item.details.depth}"D` : ""}</span>
                              )}
                            </div>
                          </div>
                          <div className="font-semibold">
                            {formatCurrency((item.price || 0) * (item.quantity || 0))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pricing Breakdown */}
              {selectedOrder.pricing_breakdown && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Pricing Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 text-sm">
                      {selectedOrder.pricing_breakdown.subtotal != null && (
                        <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{formatCurrency(selectedOrder.pricing_breakdown.subtotal)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.delivery_cost > 0 && (
                        <div className="flex justify-between"><span className="text-slate-600">Delivery</span><span>{formatCurrency(selectedOrder.pricing_breakdown.delivery_cost)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.assembly_cost > 0 && (
                        <div className="flex justify-between"><span className="text-slate-600">Assembly</span><span>{formatCurrency(selectedOrder.pricing_breakdown.assembly_cost)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.total_cost != null && (
                        <div className="flex justify-between pt-1 border-t"><span className="text-slate-700 font-medium">Total</span><span className="font-medium">{formatCurrency(selectedOrder.pricing_breakdown.total_cost)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.discount_amount > 0 && (
                        <div className="flex justify-between text-green-600"><span>Discount (20%)</span><span>-{formatCurrency(selectedOrder.pricing_breakdown.discount_amount)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.discount_amount > 0 && selectedOrder.pricing_breakdown.total_cost != null && (
                        <div className="flex justify-between pt-1 border-t font-bold text-base">
                          <span>Final Total</span>
                          <span className="text-green-700">{formatCurrency((selectedOrder.pricing_breakdown.total_cost || 0) - (selectedOrder.pricing_breakdown.discount_amount || 0))}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* SMS Sent */}
              {selectedOrder.sms_sent?.sent && (
                <Card className="border-green-200 bg-green-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      SMS Sent to {selectedOrder.sms_sent.phone}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOrder.sms_sent.message}</p>
                    {selectedOrder.sms_sent.timestamp && (
                      <p className="text-xs text-slate-500 mt-2">{formatDate(selectedOrder.sms_sent.timestamp)}</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{selectedOrder.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Photos */}
              {((selectedOrder.photos && selectedOrder.photos.length > 0) || 
                (selectedOrder.attachments && selectedOrder.attachments.length > 0)) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photos ({(selectedOrder.photos?.length || 0) + (selectedOrder.attachments?.length || 0)})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedOrder.photos?.map((photoUrl, index) => (
                        <a 
                          key={`photo-${index}-${photoUrl?.slice(-10)}`}
                          href={photoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="relative group"
                          data-testid={`order-photo-link-${index}`}
                        >
                          <img 
                            src={photoUrl} 
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border hover:border-indigo-400 transition-colors"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full h-32 bg-slate-100 rounded-lg items-center justify-center text-slate-400 text-sm">
                            Image unavailable
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                            <ExternalLink className="w-6 h-6 text-white" />
                          </div>
                        </a>
                      ))}
                      {selectedOrder.attachments?.map((attachment, index) => (
                        <a 
                          key={`attach-${index}-${attachment.name || index}`}
                          href={attachment.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="relative group"
                          data-testid={`order-attachment-link-${index}`}
                        >
                          <img 
                            src={attachment.url} 
                            alt={attachment.name || `Attachment ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border hover:border-indigo-400 transition-colors"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full h-32 bg-slate-100 rounded-lg items-center justify-center text-slate-400 text-sm">
                            Image unavailable
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                            <ExternalLink className="w-6 h-6 text-white" />
                          </div>
                          {attachment.name && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 rounded-b-lg truncate">
                              {attachment.name}
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              </div>
            {/* Fixed footer */}
            <div className="shrink-0 border-t bg-white px-6 py-3 flex items-center justify-between">
              <span className="text-xs text-slate-500">Source: {selectedOrder.source}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedOrder.lead_id && (
                  <Button variant="outline" size="sm" onClick={() => { setShowDetailModal(false); navigate(`/leads/${selectedOrder.lead_id}`); }} data-testid="view-lead-btn">
                    <ExternalLink className="w-3 h-3 mr-1" /> View Lead
                  </Button>
                )}
                <Button size="sm" onClick={() => { setShowDetailModal(false); setShowOrderActionModal(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="view-order-btn">
                  <Package className="w-3 h-3 mr-1" /> View Order
                </Button>
                <Button size="sm" onClick={() => { setShowDetailModal(false); setShowSendProductionModal(true); }} className="bg-orange-600 hover:bg-orange-700" data-testid="send-production-btn">
                  <Factory className="w-3 h-3 mr-1" /> Send to Production
                </Button>
              </div>
            </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Order Action Modal - Download PDF or View Full */}
      <Dialog open={showOrderActionModal} onOpenChange={setShowOrderActionModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">How would you like to view this order?</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            {selectedOrder?.pdf_url ? (
              <a
                href={selectedOrder.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowOrderActionModal(false)}
                className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                data-testid="download-pdf-option"
              >
                <FileDown className="w-6 h-6 text-orange-600 shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Download PDF</p>
                  <p className="text-xs text-slate-500">Download the supplier-ready PDF</p>
                </div>
              </a>
            ) : (
              <div className="flex items-center gap-3 p-4 border rounded-lg bg-slate-50 opacity-60">
                <FileDown className="w-6 h-6 text-slate-400 shrink-0" />
                <div>
                  <p className="font-medium text-slate-500">Download PDF</p>
                  <p className="text-xs text-slate-400">No PDF available for this order</p>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                setShowOrderActionModal(false);
                setShowFullOrderView(true);
              }}
              className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors text-left"
              data-testid="view-full-option"
            >
              <Eye className="w-6 h-6 text-indigo-600 shrink-0" />
              <div>
                <p className="font-medium text-slate-900">View Full Order</p>
                <p className="text-xs text-slate-500">View the complete order details</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Order View Modal - Document-style layout */}
      <Dialog open={showFullOrderView} onOpenChange={(open) => { setShowFullOrderView(open); if (!open) setOrderZoom(80); }}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-hidden p-0 flex flex-col">
          {/* Zoom control bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b shrink-0">
            <span className="text-xs text-slate-500 font-medium">Zoom: {orderZoom}%</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOrderZoom(Math.max(50, orderZoom - 10))} data-testid="order-zoom-out-button">-</Button>
              <input type="range" min="50" max="120" value={orderZoom} onChange={(e) => setOrderZoom(Number(e.target.value))} className="w-32 h-1 accent-indigo-600" data-testid="zoom-slider" />
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOrderZoom(Math.min(120, orderZoom + 10))} data-testid="order-zoom-in-button">+</Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOrderZoom(80)} data-testid="order-zoom-reset-button">Reset</Button>
            </div>
          </div>
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto" style={{ fontSize: `${orderZoom}%` }}>
          {selectedOrder && (
            <div className="bg-white" data-testid="full-order-view">
              {/* Document Header */}
              <div className="bg-slate-800 text-white p-6">
                <h2 className="text-xl font-bold">Order Specification</h2>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>Order #{selectedOrder.order_id?.slice(-8).toUpperCase()}</p>
                    {selectedOrder.quote_id && <p>Quote: {selectedOrder.quote_id}</p>}
                    <p>{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{formatCurrency(selectedOrder.order_value)}</p>
                    <p className="text-sm text-slate-300 capitalize">{selectedOrder.order_type?.replace(/_/g, " ")}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Quote Details */}
                {(selectedOrder.door_style || selectedOrder.finish || selectedOrder.cabinet_count || selectedOrder.delivery_option || selectedOrder.payment_option) && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Quote Details</h3>
                    <div className="bg-indigo-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {selectedOrder.door_style && (
                        <div><span className="text-slate-500 block text-xs">Door Style</span><span className="font-medium">{selectedOrder.door_style}</span></div>
                      )}
                      {selectedOrder.finish && (
                        <div><span className="text-slate-500 block text-xs">Finish</span><span className="font-medium">{selectedOrder.finish}</span></div>
                      )}
                      {selectedOrder.cabinet_count && (
                        <div><span className="text-slate-500 block text-xs">Cabinets</span><span className="font-medium">{selectedOrder.cabinet_count}</span></div>
                      )}
                      {selectedOrder.delivery_option && (
                        <div><span className="text-slate-500 block text-xs">Delivery</span><span className="font-medium capitalize">{selectedOrder.delivery_option?.replace(/_/g, " ")}</span></div>
                      )}
                      {selectedOrder.payment_option && (
                        <div><span className="text-slate-500 block text-xs">Payment</span><span className="font-medium capitalize">{selectedOrder.payment_option?.replace(/_/g, " ")}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customer Information */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Customer Information</h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{selectedOrder.customer_name}</span>
                    </div>
                    {selectedOrder.customer_email && (
                      <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-slate-400" /><span>{selectedOrder.customer_email}</span></div>
                    )}
                    {selectedOrder.customer_phone && (
                      <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-slate-400" /><span>{selectedOrder.customer_phone}</span></div>
                    )}
                    {selectedOrder.customer_address && (
                      <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-slate-400" /><span>{selectedOrder.customer_address}</span></div>
                    )}
                  </div>
                </div>

                {/* Order Items Table */}
                {selectedOrder.items && selectedOrder.items.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Order Items</h3>
                    <table className="hidden w-full border-collapse sm:table">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left text-xs font-semibold text-slate-600 p-2 border">Product Code</th>
                          <th className="text-left text-xs font-semibold text-slate-600 p-2 border">Item</th>
                          <th className="text-left text-xs font-semibold text-slate-600 p-2 border">Dimensions</th>
                          <th className="text-center text-xs font-semibold text-slate-600 p-2 border">Qty</th>
                          <th className="text-right text-xs font-semibold text-slate-600 p-2 border">Unit Price</th>
                          <th className="text-right text-xs font-semibold text-slate-600 p-2 border">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, idx) => (
                          <tr key={`row-${item.name}-${idx}`} className="border-b">
                            <td className="p-2 border text-xs font-mono text-slate-500">{item.item_code || item.product_sku || item.details?.item_code || item.details?.code || "—"}</td>
                            <td className="p-2 border text-sm font-medium">{item.name}</td>
                            <td className="p-2 border text-xs text-slate-600">
                              {item.details?.width && item.details?.height
                                ? `${item.details.width}"W x ${item.details.height}"H${item.details.depth ? ` x ${item.details.depth}"D` : ""}`
                                : "—"}
                            </td>
                            <td className="p-2 border text-sm text-center">{item.quantity}</td>
                            <td className="p-2 border text-sm text-right">{formatCurrency(item.price)}</td>
                            <td className="p-2 border text-sm text-right font-medium">{formatCurrency((item.price || 0) * (item.quantity || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="divide-y border border-slate-200 sm:hidden" data-testid="full-order-items-mobile-list">
                      {selectedOrder.items.map((item, idx) => (
                        <div key={`mobile-row-${item.name}-${idx}`} className="space-y-2 p-3" data-testid={`full-order-mobile-item-${idx}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900">{item.name}</p>
                              <p className="font-mono text-xs text-slate-500">{item.item_code || item.product_sku || item.details?.item_code || item.details?.code || "No product code"}</p>
                            </div>
                            <Badge variant="outline">Qty {item.quantity}</Badge>
                          </div>
                          <div className="flex items-end justify-between gap-3 text-xs text-slate-600">
                            <span>{item.details?.width && item.details?.height ? `${item.details.width}\"W × ${item.details.height}\"H${item.details.depth ? ` × ${item.details.depth}\"D` : ""}` : "Summary line"}</span>
                            <span className="shrink-0 font-mono font-bold text-slate-900">{formatCurrency((item.price || 0) * (item.quantity || 0))}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pricing Breakdown */}
                {selectedOrder.pricing_breakdown ? (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Pricing Breakdown</h3>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                      {selectedOrder.pricing_breakdown.subtotal != null && (
                        <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{formatCurrency(selectedOrder.pricing_breakdown.subtotal)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.delivery_cost > 0 && (
                        <div className="flex justify-between"><span className="text-slate-600">Delivery</span><span>{formatCurrency(selectedOrder.pricing_breakdown.delivery_cost)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.assembly_cost > 0 && (
                        <div className="flex justify-between"><span className="text-slate-600">Assembly</span><span>{formatCurrency(selectedOrder.pricing_breakdown.assembly_cost)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.total_cost != null && (
                        <div className="flex justify-between pt-2 border-t font-medium"><span>Total</span><span>{formatCurrency(selectedOrder.pricing_breakdown.total_cost)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.discount_amount > 0 && (
                        <div className="flex justify-between text-green-600"><span>Discount (20%)</span><span>-{formatCurrency(selectedOrder.pricing_breakdown.discount_amount)}</span></div>
                      )}
                      {selectedOrder.pricing_breakdown.discount_amount > 0 && selectedOrder.pricing_breakdown.total_cost != null && (
                        <div className="flex justify-between pt-2 border-t font-bold text-base">
                          <span>Final Total</span>
                          <span className="text-green-700">{formatCurrency((selectedOrder.pricing_breakdown.total_cost || 0) - (selectedOrder.pricing_breakdown.discount_amount || 0))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-4 flex justify-between font-semibold">
                    <span>Total</span><span>{formatCurrency(selectedOrder.order_value)}</span>
                  </div>
                )}

                {/* SMS Sent */}
                {selectedOrder.sms_sent?.sent && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">SMS Notification</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2">
                        <Smartphone className="w-4 h-4" />
                        Sent to {selectedOrder.sms_sent.phone}
                        {selectedOrder.sms_sent.timestamp && (
                          <span className="text-xs text-green-500 ml-auto">{formatDate(selectedOrder.sms_sent.timestamp)}</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOrder.sms_sent.message}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedOrder.notes && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Notes</h3>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOrder.notes}</p>
                    </div>
                  </div>
                )}

                {/* Photos */}
                {selectedOrder.photos && selectedOrder.photos.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Photos</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedOrder.photos.map((url, idx) => (
                        <a key={`img-${idx}-${url?.slice(-12)}`} href={url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`full-order-photo-link-${idx}`}>
                          <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-28 object-cover rounded-lg border" onError={(e) => { e.target.style.display = 'none'; }} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* End of scrollable content */}
              </div>
            </div>
          )}
          </div>
          {/* Fixed bottom action bar */}
          {selectedOrder && (
          <div className="shrink-0 border-t bg-white px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="order-specification-toolbar">
            <span className="text-xs text-slate-400" data-testid="order-specification-source">Source: {selectedOrder.source}</span>
            <div className="flex flex-wrap items-center gap-2">
              {selectedOrder.pdf_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={selectedOrder.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="order-specification-download-pdf">
                    <FileDown className="w-3 h-3 mr-1" />
                    Download PDF
                  </a>
                </Button>
              )}
              {selectedOrder.lead_id && (
                <Button variant="outline" size="sm" onClick={() => { setShowFullOrderView(false); navigate(`/leads/${selectedOrder.lead_id}`); }} data-testid="order-specification-view-lead-button">
                  View Lead
                </Button>
              )}
              {isLegacyManualTarget(selectedOrder) && (
                <LegacyManualSizeImport
                  order={selectedOrder}
                  onImported={transfer => {
                    const fields = {
                      project_id: transfer.project.project_id,
                      production_sheet_id: transfer.production_sheet.sheet_id,
                      production_sheet: transfer.production_sheet,
                      manual_size_import: transfer.manual_size_import,
                    };
                    setSelectedOrder(current => ({ ...current, ...fields }));
                    setOrders(current => current.map(order => order.order_id === selectedOrder.order_id ? { ...order, ...fields } : order));
                  }}
                />
              )}
              <Button
                size="sm"
                onClick={sendOrderToProductionSheet}
                disabled={routingToProductionSheet || isProductionDispatched}
                className={isProductionDispatched ? "border border-emerald-300 bg-emerald-50 text-emerald-800 disabled:opacity-100" : "bg-indigo-600 text-white hover:bg-indigo-700"}
                data-testid="send-order-to-production-sheet-button"
              >
                {routingToProductionSheet ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating Production Sheet…</> : isProductionDispatched ? "✓ Dispatched to Production" : "🛠️ Send to Production Sheet"}
              </Button>
              {selectedOrder.production_sheet_id && (
                <Button variant="outline" size="sm" onClick={() => { setShowFullOrderView(false); navigate(`/production-sheets/${selectedOrder.production_sheet_id}`); }} data-testid="open-dispatched-production-sheet-button">
                  <ExternalLink className="mr-1 h-4 w-4" /> Open Sheet
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowFullOrderView(false)} data-testid="close-order-specification-button">
                Close
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send to Production Modal */}
      <Dialog open={showSendProductionModal} onOpenChange={(open) => { setShowSendProductionModal(open); if (!open) { setProductionTab("aline"); setSelectedProductionSuppliers([]); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-orange-600" />
              Send to Production
            </DialogTitle>
            <DialogDescription>
              Choose a production channel and confirm the supplier recipients for this order.
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">PO Number: <span className="font-mono font-bold">{selectedOrder.order_id?.replace("order_", "PO-").toUpperCase()}</span></p>

              {/* Production Type Tabs */}
              <div className="flex border rounded-lg p-1 gap-1">
                {[
                  { id: "aline", label: "ALINE", desc: "Cabinet orders" },
                  { id: "doors", label: "DOORS", desc: "Door suppliers" },
                  { id: "finishing", label: "FINISHING", desc: "Paint/finish" },
                ].map(tab => (
                  <button key={tab.id} onClick={() => { setProductionTab(tab.id); setSelectedProductionSuppliers([]); fetchProductionSuppliers(tab.id); }}
                    className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${productionTab === tab.id ? "bg-orange-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                    data-testid={`prod-tab-${tab.id}`}>
                    <div>{tab.label}</div>
                    <div className={`text-[10px] ${productionTab === tab.id ? "text-orange-200" : "text-slate-400"}`}>{tab.desc}</div>
                  </button>
                ))}
              </div>

              {/* ALINE Tab */}
              {productionTab === "aline" && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-blue-700">Sends to: csr2_toronto@alinecabinetry.ca</p>
                    <p className="text-blue-600 text-xs mt-1">All order items with dimensions (no pricing, no customer info)</p>
                  </div>
                  <p className="text-xs text-slate-500">{selectedOrder.items?.length || 0} items will be included</p>
                </div>
              )}

              {/* DOORS / FINISHING Tab */}
              {(productionTab === "doors" || productionTab === "finishing") && (
                <div className="space-y-3">
                  {productionTab === "finishing" && (
                    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <input type="checkbox" id="paint-both-sides" className="w-4 h-4 rounded" data-testid="paint-both-sides" />
                      <label htmlFor="paint-both-sides" className="text-xs text-amber-700">Paint both sides (doubles face area + edges calculated once)</label>
                    </div>
                  )}
                  <p className="text-xs font-medium text-slate-500">Select suppliers to send to:</p>
                  {productionSuppliers.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {productionSuppliers.map(s => (
                        <label key={s.supplier_id} className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${selectedProductionSuppliers.includes(s.supplier_id) ? "bg-orange-100 border border-orange-300" : "hover:bg-slate-50 border border-transparent"}`}>
                          <input type="checkbox" checked={selectedProductionSuppliers.includes(s.supplier_id)}
                            onChange={() => {
                              setSelectedProductionSuppliers(prev => prev.includes(s.supplier_id) ? prev.filter(id => id !== s.supplier_id) : [...prev, s.supplier_id]);
                            }} className="w-4 h-4 rounded text-orange-600" data-testid={`production-supplier-checkbox-${s.supplier_id}`} />
                          <div>
                            <p className="text-sm font-medium">{s.company_name}</p>
                            <p className="text-xs text-slate-500">{s.contact_name} • {s.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No {productionTab} suppliers found. Add them in Suppliers page.</p>
                  )}
                </div>
              )}

              {/* Send Button */}
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowSendProductionModal(false)} className="flex-1" data-testid="cancel-send-production-button">Cancel</Button>
                <Button
                  onClick={async () => {
                    setSendingProduction(true);
                    try {
                      const paintBothSides = document.getElementById("paint-both-sides")?.checked || false;
                      const res = await axios.post(`${API}/orders/${selectedOrder.order_id}/send-production`, {
                        production_type: productionTab,
                        supplier_ids: selectedProductionSuppliers,
                        paint_both_sides: paintBothSides,
                      });
                      const sentCount = Number(res.data.sent_count || 0);
                      if (sentCount === 0) {
                        toast.error(res.data.message || "No supplier emails were delivered");
                      } else if (res.data.success === false) {
                        toast.warning(res.data.message || "Some supplier emails could not be delivered");
                      } else {
                        toast.success(res.data.message);
                      }
                      if (sentCount > 0) setShowSendProductionModal(false);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to send");
                    } finally {
                      setSendingProduction(false);
                    }
                  }}
                  disabled={sendingProduction || (productionTab !== "aline" && selectedProductionSuppliers.length === 0)}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  data-testid="confirm-send-production"
                >
                  {sendingProduction ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send to {productionTab.toUpperCase()}</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
